/**
 * Where "back to the list" should go from a run page.
 *
 * Hawk's list pages remember themselves (path + query) while they are on
 * screen, so a run opened from `/jobs?scope=all&status=running`, or from Eval
 * Sets or Samples, returns to exactly that view — across in-app navigation and
 * the full document loads into and out of the embedded viewers (sessionStorage
 * survives both, per tab). Deep links with nothing remembered go to Jobs.
 */
const KEY = 'hawk:return-to';

const LISTS: Record<string, string> = {
  '/jobs': 'Jobs',
  '/eval-sets': 'Eval Sets',
  '/samples': 'Samples',
  '/scans': 'Scans',
  '/search': 'Transcript Search',
};

export interface ReturnTarget {
  to: string;
  label: string;
}

const DEFAULT: ReturnTarget = { to: '/jobs', label: 'Jobs' };

export function isListRoute(pathname: string): boolean {
  return pathname in LISTS;
}

/** Called by the shell whenever a list page is shown. */
export function rememberListLocation(pathname: string, search: string): void {
  if (!isListRoute(pathname)) return;
  try {
    sessionStorage.setItem(KEY, pathname + search);
  } catch {
    // Storage unavailable (private mode / sandboxed): fall back to defaults.
  }
}

/** The remembered list, validated against the known list routes. */
export function returnTarget(): ReturnTarget {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(KEY);
  } catch {
    return DEFAULT;
  }
  if (!stored) return DEFAULT;
  const pathname = stored.split('?')[0];
  const label = LISTS[pathname];
  // Only same-origin list paths ever get stored, but re-check on read so a
  // tampered or stale value can't send the user anywhere else: the path must
  // be one of the known lists, whatever query follows it.
  if (!label) return DEFAULT;
  return { to: stored, label };
}
